import type { FC, ReactNode } from "react";
import type { BlogPostMeta } from "@content/blog/types";
import { formatPostDate } from "@content/blog";

interface SingleBlogProps {
  meta: BlogPostMeta;
  body: ReactNode;
}

const SingleBlog: FC<SingleBlogProps> = ({ meta, body }) => {
  const shareUrl = `https://maticrobots.com/blog/${meta.slug}`;
  const encodedTitle = encodeURIComponent(meta.title);
  const encodedUrl = encodeURIComponent(shareUrl);

  return (
    <div className="mx-auto mt-[24px] max-w-[900px] px-[20px] lg:mt-[40px]">
      {meta.headerMedia && (
        <img
          src={meta.headerMedia}
          alt="Blog post header"
          className="mx-auto mb-8 h-auto max-h-[400px] w-auto max-w-full rounded-lg"
        />
      )}
      <div className="mb-8 flex flex-col gap-[16px] text-center">
        <h1 className="font-weight-400 text-center text-[28px] lg:text-[44px]">
          {meta.title}
        </h1>
        {meta.subtitle && (
          <p className="font-weight-400 text-[18px] text-black/70 lg:text-[22px]">
            {meta.subtitle}
          </p>
        )}
        <div className="flex flex-col gap-[4px]">
          <p className="font-weight-400 text-[18px]">
            By {meta.author} • {formatPostDate(meta.publishedAt)}
          </p>
          {meta.updatedAt && (
            <p className="font-weight-400 text-[16px] text-black/60">
              Updated on {formatPostDate(meta.updatedAt)}
            </p>
          )}
        </div>
        <div className="flex flex-row justify-center gap-[16px]">
          <a
            href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share this post on X"
            className="share-button twitter-share"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 22 18"
              style={{
                width: "22px",
                height: "22px",
                display: "block",
                fill: "rgba(0, 0, 0, 0.6)",
                flexShrink: "0",
              }}
            >
              <path d="m22 2.12799c-.8083.36413-1.6798.60642-2.5899.71441.9321-.56211 1.6455-1.45374 1.9851-2.514271-.8716.51919-1.8393.899931-2.8648 1.103451-.8248-.883314-1.9974-1.43158-3.2951-1.43158-2.4922 0-4.5102 2.03384-4.5102 4.54257 0 .35444.0385.70057.1154 1.03423-3.75002-.19106-7.07532-1.99923-9.30088-4.750248-.38628.670098-.610352 1.453738-.610352 2.284438 0 1.57557.794552 2.96839 2.005622 3.7811-.73956-.02216-1.43514-.22983-2.045487-.56627v.05677c0 2.20275 1.554737 4.04141 3.620847 4.45671-.37803.1039-.77806.1606-1.19046.1606-.29142 0-.57185-.0304-.85091-.0817.57598 1.8041 2.24207 3.1193 4.2147 3.1581-1.54649 1.2198-3.48888 1.9466-5.60585 1.9466-.365658 0-.721694-.0221-1.07773-.065 2.0015 1.2931 4.37278 2.0421 6.92002 2.0421 8.29878 0 12.84068-6.9267 12.84068-12.9327 0-.19937-.0041-.3932-.0124-.58841.8812-.63549 1.6455-1.43712 2.2503-2.34952z"></path>
            </svg>
          </a>
          <a
            href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share this post on LinkedIn"
            className="share-button linkedin-share"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 20 18"
              style={{
                width: "22px",
                height: "22px",
                display: "block",
                fill: "rgba(0, 0, 0, 0.6)",
                flexShrink: "0",
              }}
            >
              <path
                clipRule="evenodd"
                d="m2.0769 4.15379c1.14644 0 2.07689-.93045 2.07689-2.07689 0-1.14645-.93045-2.0769-2.07689-2.0769-1.14645 0-2.0769.93045-2.0769 2.0769 0 1.14644.93045 2.07689 2.0769 2.07689zm-2.0769 13.84621v-12.46143h4.15379v12.46143zm6.92371-12.46143h3.83119v1.96336h.0554c.533-.95675 1.8373-1.96336 3.7827-1.96336 4.0444 0 4.7921 2.5172 4.7921 5.79173v6.6697h-3.9946v-5.9123c0-1.4095-.0291-3.22471-2.0769-3.22471-2.0796 0-2.3981 1.53551-2.3981 3.12231v6.0147h-3.99179z"
                fillRule="evenodd"
              ></path>
            </svg>
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share this post on Facebook"
            className="share-button fb-share"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 18 18"
              style={{
                width: "22px",
                height: "22px",
                display: "block",
                fill: "rgba(0, 0, 0, 0.6)",
                flexShrink: "0",
              }}
            >
              <path d="M17.999 8.999c0-4.97-4.029-8.999-8.999-8.999s-8.999 4.029-8.999 8.999c0 4.492 3.291 8.215 7.593 8.89v-6.289h-2.285v-2.601h2.285v-1.983c0-2.255 1.344-3.501 3.399-3.501 0.985 0 2.015 0.176 2.015 0.176v2.215h-1.135c-1.118 0-1.467 0.694-1.467 1.405v1.688h2.496l-0.399 2.601h-2.097v6.289c4.302-0.675 7.593-4.398 7.593-8.89z"></path>
            </svg>
          </a>
          <a
            href={`mailto:?body=${encodedTitle}%0A${encodedUrl}&subject=${encodedTitle}`}
            rel="noopener noreferrer"
            target="_blank"
            aria-label="Share this post by email"
            className="share-button"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 18"
              style={{
                width: "22px",
                height: "22px",
                display: "block",
                fill: "rgba(0, 0, 0, 0.6)",
                flexShrink: "0",
              }}
            >
              <path d="m12.0158 9.96411c.4532 0 .8643-.21201 1.3492-.67639l9.2121-8.782953c-.4216-.333146-1.1489-.504767-2.1713-.504767h-17.0224c-.917 0-1.58103.161526-1.97102.474481l9.24372 8.813239c.4849.46438.8959.67639 1.3597.67639zm-11.710135 6.25909 7.673255-7.29892-7.641634-7.22826c-.221344.35333-.337286.91867-.337286 1.71621v11.16547c0 .7672.105402 1.3124.305665 1.6455zm23.399235-.0101c.1897-.3432.2951-.8783.2951-1.6354v-11.16547c0-.77735-.1159-1.35278-.3373-1.69602l-7.6205 7.20807zm-20.1107 1.7869h17.0224c.9592 0 1.6443-.1817 2.0553-.525l-7.8102-7.4402-.6114.5754c-.7378.6865-1.444 1.0095-2.2345 1.0095-.801 0-1.5072-.323-2.24505-1.0095l-.61133-.5754-7.79974 7.4301c.44269.3534 1.19104.5351 2.23452.5351z"></path>
            </svg>
          </a>
        </div>
      </div>

      <div className="prose prose-lg my-[100px] max-w-none">{body}</div>
    </div>
  );
};

export default SingleBlog;
